import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Container, MenuItem, Paper, Select, Slider, Stack, TextField, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PendulumCanvas from './PendulumCanvas';
import PendulumNotebook, { type PendulumReading } from './PendulumNotebook';
import GraphPlot from './GraphPlot';
import StepGuide from './StepGuide';
import { gravityFromSlope } from './pendulumMath';
import { generatePendulumReport } from './ReportGenerator';

const PendulumLab: React.FC = () => {
  const [lengthM, setLengthM] = useState(1.0);
  const [angleDeg, setAngleDeg] = useState(8);
  const [dampingCoefficient, setDampingCoefficient] = useState(0.02);
  const [massG, setMassG] = useState(100);
  const [mode, setMode] = useState<'classic' | 'mass' | 'amplitude' | 'damping' | 'error-investigation'>('classic');
  const [readings, setReadings] = useState<PendulumReading[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [darkMode, setDarkMode] = useState(false);

  const graphPoints = useMemo(() => readings.map((row) => ({ x: row.lengthCm / 100, y: row.periodSquaredS2 })), [readings]);

  const autoRecord = (): PendulumReading | null => {
    if (!readings.length) {
      const record: PendulumReading = {
        id: `${Date.now()}`,
        lengthCm: lengthM * 100,
        oscillations: 20,
        totalTimeS: 2 * Math.PI * Math.sqrt(lengthM / 9.81) * 20,
        angleDeg,
        massG,
        periodS: 2 * Math.PI * Math.sqrt(lengthM / 9.81),
        periodSquaredS2: (2 * Math.PI * Math.sqrt(lengthM / 9.81)) ** 2,
        remarks: 'Auto-recorded from the live simulation',
      };
      return record;
    }

    const last = readings[readings.length - 1];
    const record: PendulumReading = {
      ...last,
      id: `${Date.now()}`,
      lengthCm: lengthM * 100,
      angleDeg,
      massG,
      periodS: 2 * Math.PI * Math.sqrt(lengthM / 9.81),
      periodSquaredS2: (2 * Math.PI * Math.sqrt(lengthM / 9.81)) ** 2,
      remarks: 'Auto-recorded after adjustment',
    };
    return record;
  };

  const slopeResult = useMemo(() => {
    if (graphPoints.length >= 2) {
      const fit = graphPoints.reduce((acc, point) => ({ slope: acc.slope + point.x * point.y, count: acc.count + 1 }), { slope: 0, count: 0 });
      return fit.count > 0 ? fit.slope / fit.count : 0;
    }
    return 0;
  }, [graphPoints]);

  const calculatedG = slopeResult > 0 ? gravityFromSlope(slopeResult) : null;

  const exportPdf = () => {
    const blob = generatePendulumReport({
      studentName: 'Student',
      readings,
      conclusion: `The simple pendulum experiment was carried out using a range of lengths. The graph of T² against L yielded a slope of ${slopeResult.toFixed(4)}, giving g = ${calculatedG ? calculatedG.toFixed(2) : '—'} m s⁻². The period increases with length, and small-angle conditions were maintained throughout the practical.`,
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'simple-pendulum-report.pdf';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }} className={darkMode ? 'pendulum-lab-dark' : 'pendulum-lab'}>
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #eff6ff, #f8fafc)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Simple Pendulum Laboratory</Typography>
            <Typography variant="body1" color="text.secondary">
              Measure length, time multiple oscillations, and determine g from a T² vs L plot.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={darkMode ? 'Dark mode' : 'Light mode'} color={darkMode ? 'secondary' : 'primary'} size="small" />
            <Button variant="outlined" size="small" onClick={() => setDarkMode((value) => !value)}>
              {darkMode ? 'Light UI' : 'Dark UI'}
            </Button>
            <Button variant="outlined" size="small" startIcon={<RestartAltIcon />} onClick={() => setReadings([])}>Reset</Button>
            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={exportPdf}>PDF</Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Box sx={{ flex: 1.5 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Apparatus controls</Typography>
            <Stack spacing={2}>
              <Box>
                <Typography gutterBottom>Length (m)</Typography>
                <Slider value={lengthM} min={0.2} max={2.0} step={0.05} onChange={(_, value) => setLengthM(Number(value))} valueLabelDisplay="auto" />
              </Box>
              <Box>
                <Typography gutterBottom>Angle (°)</Typography>
                <Slider value={angleDeg} min={2} max={25} step={1} onChange={(_, value) => setAngleDeg(Number(value))} valueLabelDisplay="auto" />
              </Box>
              <Box>
                <Typography gutterBottom>Damping</Typography>
                <Slider value={dampingCoefficient} min={0} max={0.1} step={0.005} onChange={(_, value) => setDampingCoefficient(Number(value))} valueLabelDisplay="auto" />
              </Box>
              <TextField label="Bob mass (g)" type="number" value={massG} onChange={(e) => setMassG(Number(e.target.value))} />
            </Stack>
          </Paper>

          <PendulumCanvas lengthM={lengthM} angleDeg={angleDeg} dampingCoefficient={dampingCoefficient} mass={massG / 1000} onTime={() => undefined} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <StepGuide mode={mode} currentStep={currentStep} />
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Practical mode</Typography>
            <Select value={mode} onChange={(e) => setMode(e.target.value as any)} fullWidth>
              <MenuItem value="classic">Classic g determination</MenuItem>
              <MenuItem value="mass">Mass independence</MenuItem>
              <MenuItem value="amplitude">Amplitude effects</MenuItem>
              <MenuItem value="damping">Damping</MenuItem>
              <MenuItem value="error-investigation">Sources of error</MenuItem>
            </Select>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}>Previous</Button>
              <Button variant="contained" onClick={() => setCurrentStep((prev) => Math.min(5, prev + 1))}>Next step</Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Experimental focus</Typography>
            <Typography variant="body2" color="text.secondary">
              Keep the angle below about 10° for the small-angle approximation. Time 20 oscillations from the fiducial marker and measure length to the centre of the bob for best results.
            </Typography>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Current estimate</Typography>
            <Alert severity={calculatedG ? 'info' : 'warning'}>
              {calculatedG ? `Estimated g = ${calculatedG.toFixed(2)} m s⁻²` : 'Record at least two lengths before estimating g.'}
            </Alert>
          </Paper>
        </Box>
      </Stack>

      <PendulumNotebook readings={readings} onChange={setReadings} onAutoRecord={autoRecord} onExportPdf={exportPdf} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
        <Box sx={{ flex: 1 }}>
          <GraphPlot title="T² vs L" points={graphPoints} xLabel="L (m)" yLabel="T² (s²)" fitThroughOrigin />
        </Box>
        <Box sx={{ flex: 1 }}>
          <GraphPlot title="T vs √L" points={readings.map((row) => ({ x: Math.sqrt(row.lengthCm / 100), y: row.periodS }))} xLabel="√L (m^0.5)" yLabel="T (s)" />
        </Box>
      </Stack>
    </Container>
  );
};

export default PendulumLab;
